import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import Link, { LinkPresentationProps } from "@waytek/MobiusOverrides/Link/Link";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cartLineId: string;
    scheduledShipments?: { [key: string]: any }[];
    extendedStyles?: ScheduleShipmentDetailStyles;
    variant: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

export type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ScheduleShipmentDetailStyles {
    scheduleInfoContainer?: GridContainerProps;
    scheduleInfoLabelText?: TypographyPresentationProps;
    scheduleInfoValueText?: TypographyPresentationProps;
    scheduleInfoLinkText?: LinkPresentationProps;
}

const detailStyles: ScheduleShipmentDetailStyles = {
    scheduleInfoContainer: {
        gap: 5,
        css: css`
            padding-top: 9px;
        `,
    },
    scheduleInfoLabelText: {
        size: "14px",
        lineHeight: "24px",
        weight: 600,
        css: css`
            flex: 0 0 auto;
            padding-right: 5px;
        `,
    },
    scheduleInfoValueText: {
        size: "14px",
        lineHeight: "24px",
        weight: 400,
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
};

const ScheduleShipmentDetailComponent = ({
    cartLineId,
    scheduledShipments,
    extendedStyles,
    language,
    variant,
}: Props) => {
    const [showAllShipments, setShowAllShipments] = useState(false);

    if (!scheduledShipments) {
        return null;
    }

    const [styles] = React.useState(() => mergeToNew(detailStyles, extendedStyles));

    const listLimit = variant === "condensed" ? 3 : 6;

    return (
        <GridContainer {...styles.scheduleInfoContainer}>
            <GridItem width={12}>
                <Typography {...styles.scheduleInfoLabelText}>{translate("Shipment Details")}: </Typography>
                <GridContainer gap={0}>
                    {scheduledShipments
                        .filter((line: any, idx: number) => {
                            if (showAllShipments) {
                                return true;
                            }

                            if (variant === "condensed") {
                                return idx < 3;
                            }

                            if (variant === "expanded") {
                                return idx < 6;
                            }

                            return idx < 3;
                        })
                        .map((line: any, idx: number) => (
                            <GridItem
                                width={12}
                                // eslint-disable-next-line react/no-array-index-key
                                key={`cartLine_condensed_${cartLineId}_${idx}`}
                            >
                                <Typography {...styles.scheduleInfoValueText} data-test-selector="productNumber">
                                    {line.qty}
                                    {" - "}
                                    {getLocalizedDateTime({
                                        dateTime: new Date(line.date),
                                        language,
                                    })}
                                </Typography>
                            </GridItem>
                        ))}
                    {scheduledShipments && scheduledShipments.length > listLimit && (
                        <GridItem width={12}>
                            <Link
                                {...styles.scheduleInfoLinkText}
                                onClick={() => {
                                    setShowAllShipments(!showAllShipments);
                                }}
                            >
                                {showAllShipments && translate("Hide Details")}
                                {!showAllShipments && translate("See all Details")}
                            </Link>
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(ScheduleShipmentDetailComponent);
